<template>
  <AuthContainer>
    <div class="text-center my-8">
      <div v-if="isVerifying" class="space-y-6">
        <div class="w-full center-blocs">
          <img src="@/assets/images/loader/loader.gif" class="size-20" alt="loader" />
        </div>
        <h1 class="text-3xl md:text-4xl font-semibold mb-4">
          {{ $t('auth.emailVerification.verifying') }}
        </h1>
        <p class="text-gray-700 text-lg">
          {{ $t('auth.emailVerification.pleaseWait') }}
        </p>
      </div>

      <div v-else-if="verificationStatus === 'success'" class="space-y-6">
        <h1 class="text-3xl md:text-4xl font-semibold mb-4 text-green-500">
          {{ $t('auth.emailVerification.success.title') }}
        </h1>
        <p class="text-gray-700 text-lg mb-6">
          {{ $t('auth.emailVerification.success.subtitle') }}
        </p>

        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p class="text-sm text-green-800">
            {{ $t('auth.emailVerification.success.message') }}
          </p>
        </div>

        <template>
          <div class="flex items-center justify-center space-x-2">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            <p class="text-sm text-gray-600">
              {{ $t('auth.emailVerification.success.autoLogin') }}
            </p>
          </div>

          <p class="text-sm text-gray-500 mt-4">
            {{ $t('auth.emailVerification.success.redirecting', { seconds: redirectCountdown }) }}
          </p>
        </template>

        <BaseButton width="w-full">
          <router-link append :to="{ name: RoutesKey.AUTH_SIGN_IN }">
            {{ $t('auth.emailVerification.returnToSignIn') }}
          </router-link>
        </BaseButton>
      </div>

      <div v-else-if="verificationStatus === 'expired'" class="space-y-6">
        <h1 class="text-3xl md:text-4xl font-semibold mb-4 text-orange-600">
          {{ $t('auth.emailVerification.expired.title') }} !
        </h1>
        <p class="text-gray-700 text-lg mb-6">
          {{ $t('auth.emailVerification.expired.subtitle') }}
        </p>

        <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p class="text-sm text-orange-800">
            {{ $t('auth.emailVerification.expired.message') }}
          </p>
        </div>

        <div v-if="resendError" class="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
          <p class="text-sm text-red-500 text-center">
            {{ $t('auth.emailVerification.resendError') }}
          </p>
        </div>

        <div v-if="resendSuccess" class="p-3 bg-green-50 border border-green-200 rounded-md mb-4">
          <p class="text-sm text-green-600 text-center">
            {{ $t('auth.emailVerification.resendSuccess') }}
          </p>
        </div>

        <BaseButton
          @click="resendVerificationEmail"
          :loading="isResending"
          :disabled="isResending"
          width="w-full"
        >
          {{
            isResending
              ? $t('auth.emailVerification.resending')
              : $t('auth.emailVerification.resendButton')
          }}
        </BaseButton>
      </div>

      <div v-else-if="verificationStatus === 'error'" class="space-y-6">
        <div
          class="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6"
        >
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>

        <h1 class="text-3xl md:text-4xl font-semibold mb-4 text-red-500">
          {{ $t('auth.emailVerification.error.title') }}
        </h1>
        <p class="text-gray-700 text-lg mb-6">
          {{ $t('auth.emailVerification.error.subtitle') }}
        </p>

        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p class="text-sm text-red-800">
            {{ $t('auth.emailVerification.error.message') }}
          </p>
        </div>

        <div class="space-y-3">
          <BaseButton @click="resendVerificationEmail" :loading="isResending" width="w-full">
            {{
              isResending
                ? $t('auth.emailVerification.resending')
                : $t('auth.emailVerification.resendButton')
            }}
          </BaseButton>

          <BaseButton width="w-full">
            <router-link append :to="{ name: RoutesKey.AUTH_SIGN_IN }">
              {{ $t('auth.emailVerification.returnToSignIn') }}
            </router-link>
          </BaseButton>
        </div>
      </div>
    </div>
  </AuthContainer>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import BaseButton from '@/components/ui/BaseButton.vue'
import { RoutesKey } from '@/models/enums/app/RoutesKey'
import AuthContainer from './AuthContainer.vue'
import { AuthenticationService } from '@/services/userAndAuth/AuthenticationService'
import { POSITION, useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { onMounted, ref } from 'vue'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const isVerifying = ref(true)
const verificationStatus = ref<'success' | 'expired' | 'error' | null>(null)
const isResending = ref(false)
const resendError = ref(false)
const resendSuccess = ref(false)
const redirectCountdown = ref(3)

const token = ref(route.query.token as string)
const email = ref(route.query.email as string)
const expiresAt = ref(route.query.expiresAt as string)
const hash = ref(route.query._hash as string)
const { t } = useI18n()

const isTokenExpired = () => {
  if (!expiresAt.value) return false
  const expirationTime = parseInt(expiresAt.value) * 1000
  return Date.now() > expirationTime
}

const verifyEmail = async () => {
  try {
    isVerifying.value = true

    if (isTokenExpired()) {
      verificationStatus.value = 'expired'
      isVerifying.value = false
      return
    }

    if (!token.value || !email.value || !hash.value) {
      throw new Error('Missing required parameters')
    }

    await AuthenticationService.verifyEmail({
      token: token.value,
      email: email.value,
      expiresAt: expiresAt.value,
      _hash: hash.value,
    })

    verificationStatus.value = 'success'
    toast.success(t('auth.emailVerification.success.title'), {
      position: POSITION.TOP_RIGHT,
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.message?.includes('expired') || error.response?.status === 410) {
      verificationStatus.value = 'expired'
    } else {
      verificationStatus.value = 'error'
    }

    toast.error(t('auth.emailVerification.error.title'), {
      position: POSITION.TOP_RIGHT,
    })
  } finally {
    isVerifying.value = false
  }
}

const resendVerificationEmail = async () => {
  try {
    isResending.value = true
    resendError.value = false
    resendSuccess.value = false

    await AuthenticationService.resendEmailVerifier(email.value)

    resendSuccess.value = true
    toast.success(t('auth.verifyEmail.resendSuccess'), {
      position: POSITION.TOP_RIGHT,
    })

    setTimeout(() => {
      router.push({
        name: RoutesKey.AUTH_EMAIL_VERIFIER_ASK,
        query: { email: email.value },
      })
    }, 2000)
  } catch {
    resendError.value = true
    toast.error(t('auth.verifyEmail.resendError'), {
      position: POSITION.TOP_RIGHT,
    })
  } finally {
    isResending.value = false
  }
}

onMounted(() => {
  verifyEmail()
})
</script>
